/**
 * The plan data model (spec §4).
 *
 * Three things are kept deliberately separate so scenario comparison can vary
 * assumptions and decisions while holding facts constant:
 *   - facts      : accounts, properties, incomes, debts, household
 *   - assumptions: returns, inflation, longevity  (AssumptionSet)
 *   - decisions  : retirement age, claiming age, withdrawal order, conversions
 *
 * RULE: no derived values live in `Plan`. Anything the engine computes —
 * year-N balances, tax owed, RMD amounts — is OUTPUT, not input.
 */

export const CURRENT_SCHEMA_VERSION = 1;

// ---------------------------------------------------------------------------
// Household & people
// ---------------------------------------------------------------------------

export type FilingStatus = 'single' | 'mfj' | 'hoh' | 'qw';

/** Two-letter USPS state code. Kept as a string for forward-compat; validated at the edges. */
export type StateCode = string;

export interface Household {
  people: Person[]; // 1 or 2. Built for 2 from the start.
  filingStatus: FilingStatus;
  dependents: Dependent[];
  stateOfResidence: StateCode;
  relocations: Relocation[];
  /** When set, replaces all state tax modeling with a flat effective rate. */
  customStateEffectiveRate?: number;
}

export interface Relocation {
  year: number;
  toState: StateCode;
}

export interface Dependent {
  id: string;
  label: string;
  birthYear: number;
  /** True while the dependent qualifies for the Child Tax Credit vs. credit for other dependents. */
  qualifiesForCTC?: boolean;
}

export interface Person {
  id: string;
  label: string; // "You" / "Spouse". Never require a real name.
  birthYear: number;
  birthMonth: number; // 1-12. Needed for accurate FRA and claiming math.
  retirementAge: number;
  lifeExpectancyAge: number; // per-person; drives survivor benefit modeling
  socialSecurity: SocialSecurityInput;
}

// ---------------------------------------------------------------------------
// Social Security input (facts + the person's claiming decision lives here too,
// mirrored in Decisions for scenario overrides)
// ---------------------------------------------------------------------------

export interface SocialSecurityInput {
  /**
   * Either an estimated monthly benefit at FRA (the common case, from an SSA
   * statement) OR a 35-year earnings history for full PIA computation.
   */
  estimatedMonthlyAtFRA?: number;
  earningsHistory?: { year: number; earnings: number }[];
  /** Age the person claims (62-70). Also mirrored/overridable in Decisions. */
  claimingAge: number;
  /** Whether this person is still working while claiming (earnings test). */
  stillWorkingWhileClaiming?: boolean;
}

// ---------------------------------------------------------------------------
// Accounts (all 15 types — the tax-treatment differences are the whole point)
// ---------------------------------------------------------------------------

export type AccountType =
  // Pre-tax, RMD-eligible
  | 'trad_401k'
  | 'trad_403b'
  | 'trad_457b'
  | 'trad_ira'
  // Roth
  | 'roth_401k'
  | 'roth_403b'
  | 'roth_457b'
  | 'roth_ira'
  // Special
  | 'hsa'
  | 'college_529'
  | 'inherited_ira_trad'
  | 'inherited_ira_roth'
  // After-tax
  | 'brokerage'
  | 'checking_savings'
  | 'cd_money_market'
  /** Taxable bonds/treasuries held outside a brokerage wrapper. Same tax
   *  treatment as a brokerage account; exists so a fixed-income holding reads
   *  clearly in the account list and earns the bond rate by default. */
  | 'bonds';

/** Broad tax family used all over the engine for withdrawal ordering and taxation. */
export type TaxFamily = 'pretax' | 'roth' | 'aftertax' | 'hsa' | 'education';

export interface PortfolioModel {
  equityPct: number;
  bondPct: number;
  cashPct: number;
}

export interface AccountContributions {
  employee: number;
  employerMatch?: { percentOfSalary: number; matchRate: number; cap: number };
  employerNonElective?: number;
  endAge?: number;
}

export interface InheritedIraInfo {
  decedentDeathYear: number;
  beneficiaryClass: 'eligible_designated' | 'non_eligible_designated';
  decedentHadBegunRMDs: boolean;
}

export interface Account {
  id: string;
  ownerId: string;
  type: AccountType;
  label: string;
  balance: number;
  asOfDate?: string; // surfaced in UI so stale inputs are visible
  costBasis?: number; // after-tax accounts
  contributions?: AccountContributions;
  allocation?: PortfolioModel;
  expectedReturnOverride?: number; // 0-15% (decimal, e.g. 0.06)
  inherited?: InheritedIraInfo; // inherited IRAs only
  /**
   * Employer plans only: this is the plan of the employer you'll separate from
   * at retirement. If that separation happens in or after the year you turn 55,
   * the "Rule of 55" makes THIS plan penalty-free immediately — a bridge that
   * disappears the moment the money is rolled into an IRA.
   */
  currentEmployerPlan?: boolean;
  /**
   * Roth accounts only: how much of the balance is direct contributions.
   * Under the Roth ordering rules contributions come out first, tax- AND
   * penalty-free at any age. Left undefined the engine assumes zero — the most
   * restrictive reading — rather than inventing a number.
   */
  rothContributionBasis?: number;
  /**
   * Roth accounts only: conversions already done before the plan starts, each
   * with the year it happened. Every conversion carries its own 5-year penalty
   * clock (the "conversion ladder"). The engine appends its own vintages as the
   * projection converts, so a plan with a conversion rule needs nothing here.
   */
  rothConversionVintages?: { year: number; taxableAmount: number }[];
}

// ---------------------------------------------------------------------------
// Income streams (multiple, time-phased)
// ---------------------------------------------------------------------------

export type IncomeType =
  | 'w2'
  | 'self_employment'
  | 'pension_monthly'
  | 'pension_lump_sum'
  | 'pension_cash_balance'
  | 'annuity_immediate'
  | 'annuity_deferred'
  | 'rental'
  | 'royalty'
  | 'other_passive';

export interface IncomeStream {
  id: string;
  ownerId: string;
  label: string;
  type: IncomeType;
  annualAmount: number;
  startAge: number;
  endAge: number;
  colaRate?: number; // default 0 for private pensions
  taxable: boolean;
  taxTreatment?: 'ordinary' | 'partially_taxable' | 'tax_free';
  exclusionRatio?: number; // non-qualified annuities: return of basis untaxed
  survivorElection?: {
    percentToSurvivor: number;
    benefitReduction: number;
  };
}

// ---------------------------------------------------------------------------
// Budget (three levels of detail — spec §13)
// ---------------------------------------------------------------------------

export type SpendingTier = 'essential' | 'discretionary';
export type ExpenseFrequency = 'monthly' | 'quarterly' | 'annual' | 'one_time';

export interface BudgetCategory {
  id: string;
  label: string;
  amount: number;
  frequency: ExpenseFrequency;
  tier: SpendingTier;
  startYear?: number;
  endYear?: number;
  inflationRateOverride?: number;
  deductible?: boolean;
}

export interface BudgetStep {
  /** From this year onward, base annual spend changes to `annualAmount`. */
  startYear: number;
  annualAmount: number;
  label?: string;
}

export interface Budget {
  mode: 'simple' | 'staircase' | 'detailed';
  /** simple */
  annualAmount?: number;
  retirementSpendingMultiplier?: number; // e.g. 0.85
  /** What fraction of the simple/staircase spend is essential (must-spend). */
  essentialFraction?: number;
  /** staircase */
  baseAnnualAmount?: number;
  steps?: BudgetStep[];
  /** detailed */
  categories?: BudgetCategory[];
  /** Age-banded go-go / slow-go / no-go multipliers applied to discretionary. */
  ageBands?: { startAge: number; discretionaryMultiplier: number }[];
}

// ---------------------------------------------------------------------------
// Debts
// ---------------------------------------------------------------------------

export type DebtType =
  | 'mortgage'
  | 'heloc'
  | 'auto'
  | 'student'
  | 'personal'
  | 'credit_card';

export interface Debt {
  id: string;
  label: string;
  type: DebtType;
  balance: number;
  annualRate: number; // decimal
  monthlyPayment: number;
  termMonths?: number;
  originationDate?: string;
  extraPrincipalMonthly?: number;
}

// ---------------------------------------------------------------------------
// Properties (spec §12)
// ---------------------------------------------------------------------------

export interface Property {
  id: string;
  label: string;
  type: 'primary' | 'secondary' | 'rental' | 'land';
  ownership: 'own_outright' | 'own_with_mortgage' | 'rent' | 'reverse_mortgage';
  currentValue: number;
  costBasis: number;
  purchaseYear?: number;
  plannedSaleYear?: number;
  sellingCostPct: number; // default ~0.06-0.08
  mortgageId?: string;
  propertyTaxAnnual: number;
  insuranceAnnual: number;
  hoaAnnual: number;
  maintenancePctOfValue: number;
  appreciationRateOverride?: number;
  rentalIncome?: { grossAnnual: number; vacancyPct: number; expensesAnnual: number };
}

// ---------------------------------------------------------------------------
// One-time events
// ---------------------------------------------------------------------------

export interface OneTimeEvent {
  id: string;
  year: number;
  label: string;
  amount: number; // positive = inflow, negative = outflow
  taxTreatment: 'tax_free' | 'ordinary' | 'ltcg';
  costBasis?: number;
  /**
   * Hold back an estimate of the tax this event will trigger before routing the
   * proceeds, so the amounts sent to accounts are net rather than gross and the
   * plan is not left selling assets next spring to pay the bill. The reserve
   * stays as spendable cash. Estimated, not the exact liability.
   */
  reserveTaxFromProceeds?: boolean;
  /**
   * Explicit deposit splits, applied in order and capped at what remains of
   * the inflow — e.g. "$200k tops up the SGOV bucket" before the remainder
   * moves on.
   */
  destinations?: { accountId: string; amount: number }[];
  /** Where whatever is left after the splits goes; unset = spendable cash. */
  destinationAccountId?: string;
}

// ---------------------------------------------------------------------------
// Healthcare & long-term care (spec §11)
// ---------------------------------------------------------------------------

export interface HealthcareConfig {
  /**
   * Pre-65 coverage: 'aca' estimates the premium tax credit from MAGI vs. FPL
   * (with the 400%-of-FPL cliff when in force); 'override' is a simple
   * "I'll pay $X/month" for retiree coverage, COBRA, or a spouse's plan.
   */
  preMedicareMode: 'aca' | 'override';
  /** Benchmark silver-plan premium per person, $/month (today's dollars). */
  benchmarkMonthlyPremium: number;
  /** Used when preMedicareMode is 'override'. */
  overrideMonthlyPremium: number;
  /** Out-of-pocket (deductibles, copays) per person, $/year, pre- and post-65. */
  oopAnnualPerPerson: number;
  /** Medigap / Advantage premium per person on Medicare, $/month. */
  medigapMonthly: number;
  /** Dental + vision per person, $/year. */
  dentalVisionAnnual: number;
}

export interface LongTermCareAssumption {
  personId: string;
  /** Off by default — LTC is modeled as an optional event, not a certainty. */
  enabled: boolean;
  onsetAge: number;
  durationYears: number;
  annualCostToday: number;
  careType: 'in_home' | 'assisted_living' | 'nursing_home' | 'memory_care';
  /** Historically well above CPI; falls back to the profile's ltcInflation. */
  inflationRateOverride?: number;
  insurance?: {
    dailyBenefit: number;
    benefitPeriodYears: number;
    eliminationPeriodDays: number;
    /** Annual compound inflation rider on the benefit (e.g. 0.03). */
    inflationRider: number;
    annualPremium: number;
    premiumEndAge?: number;
  };
}

// ---------------------------------------------------------------------------
// Assumptions (three profiles — spec §5)
// ---------------------------------------------------------------------------

export interface SocialSecurityHaircut {
  startYear: number;
  payableFraction: number; // e.g. 0.77
}

export interface AssumptionSet {
  label: 'optimistic' | 'average' | 'pessimistic' | string;
  returnsByAssetClass: { equity: number; bond: number; cash: number };
  generalInflation: number;
  healthcareInflation: number;
  ltcInflation: number;
  wageGrowth: number;
  housingAppreciation: number;
  socialSecurityHaircut?: SocialSecurityHaircut;
  longevityAge: number;
}

// ---------------------------------------------------------------------------
// Decisions (the levers scenarios flex)
// ---------------------------------------------------------------------------

export type WithdrawalStrategy = 'fund_spending' | 'fixed_percentage' | 'max_spending_solve';
export type WithdrawalOrderMode = 'traditional' | 'custom' | 'bracket_aware';

export interface Decisions {
  withdrawalStrategy: WithdrawalStrategy;
  fixedPercentage?: number; // for fixed_percentage strategy
  orderMode: WithdrawalOrderMode;
  customOrder?: TaxFamily[]; // for custom order mode
  bracketAwareCeiling?: number; // target top marginal rate for bracket-aware fill
  /**
   * Push accounts that would incur a 10% early-withdrawal penalty to the back
   * of the order, so they fund spending only when nothing else can. Default
   * ON: paying a penalty while penalty-free money sits unused is a sequencing
   * mistake, not a strategy. Turn it off to see the cost of naive ordering —
   * it is not a free win, since deferring to Roth spends tax-free growth.
   */
  avoidEarlyPenalties?: boolean;
  /** Optional per-person claiming-age override (else uses Person.socialSecurity). */
  claimingAgeOverride?: Record<string, number>;
  /** Manual Roth conversion schedule (explicit amounts and years). */
  rothConversions?: { year: number; amount: number; payTaxFrom: 'conversion' | 'aftertax' }[];
  /**
   * Rule-driven conversions (the explorer's output): each year in the window,
   * convert enough to fill ordinary income to the target —
   *  - 'bracket': target is a marginal rate (0.12/0.22/0.24); fill to its top.
   *  - 'irmaa':   target is a MAGI threshold in dollars; fill to just below it
   *               (the two-year premium lookback is modeled by the engine).
   */
  rothConversionRule?: {
    type: 'bracket' | 'irmaa';
    target: number;
    startYear: number;
    endYear: number;
  };
  /**
   * Safety-bucket strategy (sequence-of-returns defense): a designated
   * cash-like account is PROTECTED from the normal withdrawal order and drawn
   * FIRST after down-equity years (and, optionally, during all pre-Medicare
   * retirement years to keep MAGI low for the ACA subsidy). In up years it
   * refills back toward the inflation-indexed target from surplus cash first,
   * then from other after-tax accounts (realizing the gains that costs).
   * If everything else runs out, the bucket funds spending regardless.
   */
  bucketStrategy?: {
    accountId: string;
    /** Target bucket size in today's dollars. */
    targetToday: number;
    /** Also draw bucket-first in retired pre-65 years to protect the ACA
     *  subsidy (low-gain draws keep MAGI down). Default true. */
    protectAcaYears?: boolean;
    /**
     * Years after retirement the bucket stays active (protected, triggered,
     * refilled). Sequence risk lives in the first decade; maintaining a big
     * cash pile forever costs more in drag than a late crash would take.
     * After the window the bucket joins the normal spending pool. Default 10.
     */
    windowYears?: number;
  };
  /**
   * Tax-loss harvesting — modeled as DEFERRAL, not free money:
   *  - Down-equity years: capture `downYearCapture` of the equity decline in
   *    brokerage accounts (fires in Monte Carlo / stress, where down years
   *    exist).
   *  - `baselinePct` of brokerage balance is harvestable every year from
   *    lot-level dispersion (0 for set-and-forget investors; ~0.5-2% for
   *    active lot harvesters / direct indexing).
   *  Losses offset realized gains without limit, then up to $3,000/yr of
   *  ordinary income; the rest carries forward. Harvesting REDUCES BASIS, so
   *  future sales realize bigger gains — taxes shift later, they don't vanish.
   */
  taxLossHarvesting?: {
    enabled: boolean;
    /** Fraction of down-year taxable equity losses actually captured (default 0.75). */
    downYearCapture?: number;
    /** Harvest as a fraction of brokerage balance in ANY year (default 0). */
    baselinePct?: number;
  };
}

// ---------------------------------------------------------------------------
// The plan
// ---------------------------------------------------------------------------

export interface Plan {
  schemaVersion: number;
  household: Household;
  accounts: Account[];
  properties: Property[];
  incomes: IncomeStream[];
  budget: Budget;
  debts: Debt[];
  oneTimeEvents: OneTimeEvent[];
  /** Optional — defaults applied by the engine when absent (older plans). */
  healthcare?: HealthcareConfig;
  ltcEvents?: LongTermCareAssumption[];
  /** The three maintained profiles + which one is active for a given view. */
  assumptionProfiles: AssumptionSet[];
  activeAssumptionLabel: string;
  decisions: Decisions;
  /** Horizon override; when absent, engine derives from longevity assumptions. */
  horizonYearOverride?: number;
}
